import * as React from 'react';
import { styled, SvgIconProps, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import type { TreeItemProps } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import { Box } from '@mui/material';
import * as FolderStore from '../store/Folder';
import * as ImageStore from '../store/Image';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { Folder, Image, ImageCollection } from '../models';
import _ from 'lodash';
import { getChildrenRecursive } from '../utils';

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

const ROOT_FOLDER_ID = 'root';
const TREE_ITEM_CONTENT_CLASS = 'MuiTreeItem-content';
const TREE_ITEM_LABEL_CLASS = 'MuiTreeItem-label';
const TREE_ITEM_GROUP_CLASS = 'MuiTreeItem-group';

const CustomTreeItem = (props: TreeItemProps) => <TreeItem {...props} />;

const StyledTreeItemRoot = styled(CustomTreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [`& .${TREE_ITEM_CONTENT_CLASS}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    width: 'calc(100% - 32px)',
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-expanded': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: 'var(--tree-view-color)',
    },
    [`& .${TREE_ITEM_LABEL_CLASS}`]: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
  },
  [`& .${TREE_ITEM_GROUP_CLASS}`]: {
    [`& .${TREE_ITEM_CONTENT_CLASS}`]: {
      paddingLeft: theme.spacing(2),
    },
  },
}));

const LoadingTreeItem = styled(StyledTreeItemRoot)(({ theme }) => ({
  [`& .${TREE_ITEM_CONTENT_CLASS}`]: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'unset',
    },
    '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: 'unset',
      color: 'unset',
    },
  }
}));

type StyledTreeItemProps = TreeItemProps & {
  bgColor?: string;
  color?: string;
  labelIcon: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
};

function StyledTreeItem(props: StyledTreeItemProps) {
  const {
    bgColor,
    color,
    labelIcon: LabelIcon,
    labelInfo,
    labelText,
    ...other
  } = props;

  return (
    <StyledTreeItemRoot
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', p: 0.5, pr: 0 }}>
          <Box component={LabelIcon} color="inherit" sx={{ mr: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 'inherit', flexGrow: 1 }}>
            {labelText}
          </Typography>
          <Typography variant="caption" color="inherit">
            {labelInfo}
          </Typography>
        </Box>
      }
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      }}
      {...other}
    />
  );
}

interface FolderHierarchyProps {
  onNodeSelected?: (type: 'folder' | 'image', nodeId: string) => void;
  selectedNodeId?: string;
  foldersOnly?: boolean;
  includeRoot?: boolean;
  showImagesLoader?: boolean;
}

export default function FolderHierarchy(props: FolderHierarchyProps) {
  const [expanded, setExpanded] = React.useState<string[]>([ROOT_FOLDER_ID]);
  const dispatch = useAppDispatch();
  const folders = useSelector(FolderStore.selectors.folders);
  const foldersFlattened = getChildrenRecursive(folders, f => f.childFolders);
  const loadImages = useSelector(ImageStore.selectors.loadImages);
  const images = useSelector(ImageStore.selectors.images);
  const initialize = useSelector(ImageStore.selectors.initialize);
  const { selectedNodeId } = props;

  const getFolder = React.useCallback((nodeId: string) => {
    return foldersFlattened.find(i => i.id === nodeId);
  }, [foldersFlattened]);

  const getImage = React.useCallback((nodeId: string) => {
    const allImages = _.flatten(images.map(i => i.images));
    return allImages.find(i => i.id === nodeId);
  }, [images]);

  const isRoot = (nodeId: string) => {
    return nodeId === ROOT_FOLDER_ID;
  };

  const getSelectedNodeId = () => {
    // if the node id belongs to an image and we're not showing images in the tree, then lets select it's parent folder instead
    if (!props.foldersOnly || !selectedNodeId) {
      return selectedNodeId;
    }
    const image = getImage(selectedNodeId);
    if (image) {
      return image.folderId;
    }
    return selectedNodeId;
  }

  const isFolder = React.useCallback((nodeId: string) => {
    return Boolean(isRoot(nodeId) || getFolder(nodeId));
  }, [getFolder]);

  // component initialization
  React.useEffect(() => {
    if (!folders || folders.length === 0) {
      dispatch(FolderStore.actionCreators.loadFolders());
    }
    if (selectedNodeId && !initialize.is) {
      dispatch(ImageStore.actionCreators.initialize(selectedNodeId, (loadedImages: ImageCollection[]) => {
        // make sure all folders loaded are expanded
        const folderIds = loadedImages.map(li => li.id);
        setExpanded([...expanded.filter(id => !folderIds.includes(id)), ...folderIds, ROOT_FOLDER_ID]);
      }));
    }
  }, [dispatch]);

  React.useEffect(() => {
    let foldersToExpand = [] as string[];
    if (!selectedNodeId) {
      return;
    }
    // make sure that all parent folders for the selected node are expanded
    const folder = getFolder(selectedNodeId);
    const image = getImage(selectedNodeId);
    if (folder) {
      const parentFolders = FolderStore.utils.getParentFolders(foldersFlattened, folder);

      // add any folders that aren't already expanded
      parentFolders.forEach(folder => {
        if (!expanded.includes(folder.id)) {
          foldersToExpand.push(folder.id);
        }
      });
    } else if (image) {
      const parentFolders = ImageStore.utils.getParentFolders(foldersFlattened, image);

      // add any folders that aren't already expanded
      parentFolders.forEach(folder => {
        if (!expanded.includes(folder.id)) {
          foldersToExpand.push(folder.id);
        }
      });
    }

    // expand the folders that need to be expanded
    const newExpanded = [...expanded, ...foldersToExpand, ROOT_FOLDER_ID];
    setExpanded(newExpanded);

  }, [selectedNodeId, folders])

  const handleToggle = (event: React.SyntheticEvent, itemIds: string[]) => {
    const newlyExpandedNodeIds = itemIds.filter(nodeId => !expanded.includes(nodeId));
    setExpanded(itemIds);

    // trigger loading of files for all newly expanded folders (if we're not showing only folders)
    if (props.foldersOnly !== true) {
      newlyExpandedNodeIds.forEach(nodeId => dispatch(ImageStore.actionCreators.loadImages(nodeId)));
    }
  };

  const handleSelect = (event: React.SyntheticEvent, itemId: string | null) => {
    if (itemId && props.onNodeSelected) {
      const nodeType = isFolder(itemId) ? 'folder' : 'image';
      props.onNodeSelected(nodeType, itemId);

      if (isFolder(itemId)) {
        props.onNodeSelected('folder', itemId);
      }
    }
  };

  const isExpanded = (folderId: string) => {
    return expanded.includes(folderId);
  }

  const isLoadingContents = (folderId: string) => {
    const actionDef = loadImages.find(ad => ad.id === folderId);
    return actionDef ? actionDef.is : false;
  }

  const renderImage = (image: Image): JSX.Element => {
    return (
      <StyledTreeItem key={image.id} itemId={image.id} labelText={image.fileName} labelIcon={ImageIcon}></StyledTreeItem>
    );
  }

  const renderFolder = (folder: Folder): JSX.Element => {
    return (
      <StyledTreeItem key={folder.id} itemId={folder.id} labelText={folder.name} labelIcon={isExpanded(folder.id) ? FolderOpenIcon : FolderIcon}>
        {props.showImagesLoader === true && isLoadingContents(folder.id) ? <LoadingTreeItem itemId={'LOADING_' + folder.id} label="Loading Images..." /> : null}
        {folder.childFolders.map(renderFolder)}
        {props.foldersOnly !== true && ImageStore.utils.getImagesForFolder(images, folder.id).map(renderImage)}
      </StyledTreeItem>
    )
  };

  const renderRoot = (): JSX.Element => {
    return (
      <StyledTreeItem itemId={ROOT_FOLDER_ID} labelText={'Root'} labelIcon={isExpanded(ROOT_FOLDER_ID) ? FolderOpenIcon : FolderIcon}>
        {folders.map(renderFolder)}
      </StyledTreeItem>
    );
  }

  return (
    <>
      <SimpleTreeView
        aria-label="file system navigator"
        slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
        expandedItems={expanded}
        onExpandedItemsChange={handleToggle}
        onSelectedItemsChange={handleSelect}
        selectedItems={getSelectedNodeId()}
        sx={{ height: '100%', flexGrow: 1, maxWidth: 400, overflowY: 'auto' }}
      >
        {props.includeRoot === true ? renderRoot() : folders.map(renderFolder)}
      </SimpleTreeView>
    </>
  );
}